import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Nullable } from '~/eds/src/types/Nullable';
import { capitalize } from '~/utils/strings';

interface Props {
  entity: 'fields' | 'clauses';
  isAllSelected: boolean;
  entityNames: string[];
  selectedCount: number;
  onDeleteClick: () => void;
  isDeleteActionLoading: boolean;
}

export const useDeleteConfirmationModal = ({
  entity,
  entityNames,
  isAllSelected = false,
  selectedCount,
  onDeleteClick,
  isDeleteActionLoading,
}: Props): [ReactNode, () => void] => {
  const intl = useIntl();
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<Nullable<string>>('');

  const selectedCountText = intl.formatMessage(
    {
      id: 'DeleteConfirmationModal.selectedCount',
      defaultMessage:
        '<strong>{selectedCount} selected {selectedCount, plural, one {document} other {documents}}</strong>',
      description: 'Text of the delete confirmation modal selected count',
    },
    { selectedCount, strong: (chunks) => <strong>{chunks}</strong> },
  );

  const duplicatesCountText = intl.formatMessage(
    {
      id: 'DeleteConfirmationModal.duplicatesCount',
      defaultMessage:
        '{selectedCount, plural, one {its} other {their}} duplicate documents',
      description: 'Text of the delete confirmation modal duplicate count',
    },
    { selectedCount },
  );

  const [deleteModal, showDeleteModal] = useModal({
    title: intl.formatMessage(
      {
        id: 'DeleteConfirmationModal.title',
        defaultMessage: 'Delete {entity}',
        description: 'Title of the delete confirmation modal',
      },
      { entity },
    ),
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          <FormattedMessage
            id="DeleteConfirmationModal.deletingText"
            defaultMessage="Deleting <strong>{entityNames}</strong> for the {selectedCountText} and {duplicatesCountText} is permanent and cannot be undone."
            description="Text of the delete confirmation modal deleting text"
            values={{
              entityNames: isAllSelected
                ? intl.formatMessage({
                    id: 'DeleteConfirmationModal.allClauses',
                    defaultMessage: 'all clauses',
                    description:
                      'Text of the delete confirmation modal all clauses',
                  })
                : entityNames.join(', '),
              strong: (chunks) => <strong>{chunks}</strong>,
              selectedCountText,
              duplicatesCountText,
            }}
          />
        </Text>
        <Text>
          <FormattedMessage
            id="DeleteConfirmationModal.deleteConfirmationText"
            defaultMessage="Type 'DELETE' in the box below to proceed."
            description="Text of the delete confirmation modal delete confirmation text"
          />
        </Text>
        <TextInput
          value={deleteConfirmation}
          onChange={setDeleteConfirmation}
          name="delete-confirmation"
        ></TextInput>
      </Layout>
    ),
    primaryAction: {
      variant: 'danger',
      icon: 'trash-permanent',
      text: intl.formatMessage(
        {
          id: 'DeleteConfirmationModal.primaryAction',
          defaultMessage: 'Delete {entity}',
          description: 'Text of the delete confirmation modal primary action',
        },
        { entity: capitalize(entity) },
      ),
      disabled: deleteConfirmation !== 'DELETE',
      onClick: onDeleteClick,
      isLoading: isDeleteActionLoading,
    },
  });

  return [deleteModal, showDeleteModal];
};
